import { useQuery } from "@tanstack/react-query";
import { programsApi, ProgramSummary } from "@/services/api";
import { useAuth } from "@/hooks/useAuth";
import { hasRole } from "@/utils/roleUtils";
import { MembershipRole } from "@/constants/roles";

export interface UseExpertProgramsResult {
  programs: ProgramSummary[];
  expertPrograms: ProgramSummary[];
  advisorPrograms: ProgramSummary[];
  /** Peer Advisory groups the user PARTICIPATES in, as an accepted PEER. */
  peerPrograms: ProgramSummary[];
  /** Peer Advisory groups the user RUNS, as lead or co-facilitator. */
  facilitatedPeerPrograms: ProgramSummary[];
  isExpertAchiever: boolean;
  isExpertAdvisor: boolean;
  isAdvisor: boolean;
  isLoading: boolean;
  isError: boolean;
}

export const useExpertPrograms = (): UseExpertProgramsResult => {
  const { user } = useAuth();
  const isAdvisor = hasRole(user, "advisor");
  const isAchiever = hasRole(user, "achiever");

  const { data, isLoading, isError } = useQuery({
    queryKey: ["programs"],
    queryFn: programsApi.list,
    enabled: !!user && (isAchiever || isAdvisor),
    staleTime: 5 * 60 * 1000,
  });

  const programs = data?.programs ?? [];

  const expertPrograms = programs.filter(
    (p) => p.programType === "EXPERT" && p.role === MembershipRole.ACHIEVER,
  );

  const advisorPrograms = programs.filter(
    (p) =>
      p.programType === "EXPERT" &&
      (p.role === MembershipRole.LEAD_ADVISOR || p.role === MembershipRole.CO_ADVISOR),
  );

  // Kept as their own lists rather than folded into the two above. A peer group
  // is a different experience from an Expert Program -- members bring their own
  // goals and the cohort advises each other -- so a caller that renders "your
  // programs" should be deciding deliberately whether to include them, not
  // getting them by accident.
  const peerPrograms = programs.filter(
    (p) => p.programType === "PEER_ADVISORY" && p.role === MembershipRole.PEER,
  );

  const facilitatedPeerPrograms = programs.filter(
    (p) =>
      p.programType === "PEER_ADVISORY" &&
      (p.role === MembershipRole.LEAD_ADVISOR || p.role === MembershipRole.FACILITATOR),
  );

  return {
    programs,
    expertPrograms,
    advisorPrograms,
    peerPrograms,
    facilitatedPeerPrograms,
    isExpertAchiever: expertPrograms.length > 0,
    isExpertAdvisor: advisorPrograms.length > 0,
    isAdvisor,
    isLoading,
    isError,
  };
};
